import {UntypedFormControl, UntypedFormGroup} from '@angular/forms';
import {combineLatestObj, ensureObservable, omitNils, replayLast} from '@k2/common/helpers';
import {
  CollectionType,
  CurrencyOption,
  Field,
  Fields,
  FieldSpec,
  FieldsSpec,
  FormSpec,
  MultiFieldOption
} from '@k2/common/k2-forms-state/types';
import {requiredValidator, toValidators} from '@k2/common/k2-forms/validators';
import {mapObjIndexed, toPairs, values} from 'ramda';
import {combineLatest, isObservable, Observable, of} from 'rxjs';
import {distinctUntilChanged, first, map, startWith, switchMap} from 'rxjs/operators';
import {EventEmitter} from '@angular/core';

/**
 * Transforms a form specification to fields.
 *
 * A fields could be conditional, the output will always be an Observable.
 * This allows to conditionally update fields based on their state.
 */
export function formSpecToFields(source: FormSpec | Observable<FormSpec>): Observable<Fields> {
  const observable = ensureObservable(source);

  return observable.pipe(map(toFieldsSpec), fieldsSpecToFields);
}

/**
 * Transforms a fields specification to fields.
 *
 * A fields could be conditional, the output will always be an Observable.
 * This allows to conditionally update fields based on their state.
 */
export function fieldsSpecToFields(
  source: FieldsSpec | Observable<FieldsSpec>
): Observable<Fields> {
  const observable = ensureObservable(source);

  return observable.pipe(
    distinctUntilChanged(),
    map(mapObjIndexed(toField)),
    map(mapObjIndexed(toConditionalField)),
    switchMap(combineLatestObj),
    map(omitNils),
    replayLast()
  );
}

/**
 * Transforms a field specification to field.
 *
 * Returned field will contain a specification,
 * form control and visibility information.
 */
export function toField(spec: FieldSpec): Field {
  const { name, attributes, errors } = spec;
  const value = toInitialValue(spec);
  const validators = toValidators(spec.validators);
  const { disabled } = attributes;
  const control = new UntypedFormControl({ value, disabled }, validators);

  const buildCollection = (fieldCollectionElement: FormSpec) => {
    Object.values(fieldCollectionElement.fields).forEach((field => {
      let value = field.value;
      if(field.attributes.display_type === 'radioSwitch') {
        value = field.value === true ? fieldCollectionElement.meta.id : null;
      }

      let name = field.attributes.display_type === 'radioSwitch' ? field.name : field.name + '_' + fieldCollectionElement.meta.id;

      field.attributes.originName = field.name
      field.attributes.originVal = field.value
      field.value = value
      field.name = name
    }));
    let observableFields = fieldsSpecToFields(fieldCollectionElement.fields)

    return {
      collectionId: fieldCollectionElement.meta.id,
      fields: observableFields
    }
  }


  let fieldCollection: CollectionType[] = []
  let prototype: CollectionType = null
  if (spec.attributes.fieldCollection && (typeof spec.attributes.fieldCollection === 'object')) {
    for (const fieldCollectionElement of spec.attributes.fieldCollection) {

      let collection = buildCollection(fieldCollectionElement)
      fieldCollection.push(collection)
    }
  }

  if (spec.attributes.prototype && (typeof spec.attributes.prototype === 'object')) {
    prototype = buildCollection(spec.attributes.prototype)
  }

  return {
    control,
    name,
    attributes,
    fieldCollection,
    prototype,
    defaultValue: value === undefined ? null : JSON.parse(JSON.stringify(value)),
    errors,
    validators,
    attributeChanges: new EventEmitter<any>()
  };
}

function toInitialValue(spec: FieldSpec): any {
  const { value } = spec;
  const displayType = spec.attributes.display_type;
  if (displayType !== 'select' && displayType !== 'select2') return value;

  if (value != null && value !== '') return value;

  const { valueOptions } = spec.attributes;
  if (!Array.isArray(valueOptions) || valueOptions.length !== 1) return value;
  return valueOptions[0].id || value;
}

/**
 * Extracts a fields specification from form specification.
 */
export function toFieldsSpec(form: FormSpec): FieldsSpec {
  return form.fields;
}

/**
 * Extracts all form controls from a given fields and wraps them into a FormGroup.
 *
 * Returned Observable will cache the latest value until there is at least one subscriber.
 */
export function fieldsToForm(source: Fields): UntypedFormGroup;
export function fieldsToForm(source: Observable<Fields>): Observable<UntypedFormGroup>;

export function fieldsToForm(source) {
  const buildTopLevelControls = (fields: Fields) => {
    // add top-level controls first
    const controls: Record<string, any> = mapObjIndexed((field: Field) => field.control, fields);
    const formGroup = new UntypedFormGroup(controls)
    // for synchronous branch, include nested collections only when their `.fields` is plain object
    for (const fname in fields) {
      const field = fields[fname];
      if (field.fieldCollection && Array.isArray(field.fieldCollection) && field.fieldCollection.length > 0) {
        for (const col of field.fieldCollection) {
          // if col.fields is plain Fields (not Observable), merge them with prefix to avoid collisions
          if (!isObservable(col.fields)) {
            for (const innerName in (col.fields as Fields)) {
              (col.fields as Fields)[innerName].form = formGroup;
            }
            const innerControls = buildControlsFromFields(col.fields as Fields, `${fname}_${col.collectionId}_`);
            Object.assign(controls, innerControls);
          }
        }
      }
      if (field.prototype && (typeof field.prototype === 'object')) {
        if (!isObservable(field.prototype.fields)) {
          const innerFields = field.prototype.fields as Fields;
          for (const innerName in innerFields) {
            innerFields[innerName].form = formGroup;
          }
        }
      }
    }

    return formGroup;
  };

  // Observable branch: need to resolve nested collection `.fields` observables
  if (isObservable(source)) {
    return source.pipe(
      switchMap((fields: Fields) => {
        const baseControls: Record<string, any> = mapObjIndexed((field: Field) => field.control, fields);

        // collect inner observables that need resolving
        const innerObsMap: Record<string, Observable<Record<string, UntypedFormControl>>> = {};
        const formGroup = new UntypedFormGroup(baseControls)
        for (const fname in fields) {
          const field = fields[fname];
          if (field.fieldCollection && Array.isArray(field.fieldCollection) && field.fieldCollection.length > 0) {
            for (const col of field.fieldCollection) {
              // only handle when col.fields is Observable<Fields>
              if (isObservable(col.fields)) {
                const obsKey = `${fname}__collection__${col.collectionId}`;
                innerObsMap[obsKey] = (col.fields as Observable<Fields>).pipe(
                  map((innerFields: Fields) => {
                    // map inner fields to controls and prefix keys to avoid collisions
                    const prefixed: Record<string, UntypedFormControl> = {};
                    for (const innerName in innerFields) {
                      prefixed[`${fname}_${col.collectionId}_${innerName}`] = innerFields[innerName].control;
                      innerFields[innerName].form = formGroup;
                    }
                    return prefixed;
                  })
                );
              } else {
                // if col.fields already plain Fields, merge immediately into baseControls
                const innerFields = col.fields as Fields;
                for (const innerName in innerFields) {
                  baseControls[`${fname}_${col.collectionId}_${innerName}`] = innerFields[innerName].control;
                  innerFields[innerName].form = formGroup;
                }
              }
            }
          }
          if (field.prototype && (typeof field.prototype === 'object')) {
            if (isObservable(field.prototype.fields)) {
              (field.prototype.fields as Observable<Fields>).forEach(
                (innerFields: Fields) => {
                  for (const innerName in innerFields) {
                    innerFields[innerName].form = formGroup;
                  }
                  return true;
                }
              );
            } else {
              const innerFields = field.prototype.fields as Fields;
              for (const innerName in innerFields) {
                innerFields[innerName].form = formGroup;
              }
            }
          }
        }

        // if no inner observables, return immediate group
        if (Object.keys(innerObsMap).length === 0) {
          return of(formGroup);
        }

        // resolve all inner observables and merge their controls
        return combineLatestObj(innerObsMap).pipe(
          map(resolved => {
            for (const k in resolved) {
              Object.assign(baseControls, resolved[k]);
            }
            return formGroup;
          })
        );
      }),
      replayLast()
    );
  }

  // synchronous branch (source is plain Fields)
  return buildTopLevelControls(source as Fields);
}


/**
 * Helper: build a controls map from a Fields object.
 * Optional prefix will be prepended to each inner field name.
 */
function buildControlsFromFields(fields: Fields, prefix = ''): Record<string, UntypedFormControl> {
  return mapObjIndexed((field: Field) => field.control, fields)
    // prefix keys
    ? Object.keys(fields).reduce((acc, key) => {
      acc[`${prefix}${key}`] = fields[key].control;
      return acc;
    }, {} as Record<string, UntypedFormControl>)
    : {};
}

/**
 * Transforms a given field to conditional field.
 *
 * A conditional field has the same structure as a normal field.
 * However, it can listen to value changes in its determinator field
 * and act as described in consequences.
 *
 * If also supports attribute conditionalPrefix, which provides additional
 * determinator field for updating currency prefix.
 *
 * A field with no conditional behavior will be returned without modifications.
 */
function toConditionalField(field: Field, _, fields: Fields): Observable<Field> {
  const { conditional } = field.attributes;
  const { conditionalPrefix } = field.attributes;
  let multiSelectFieldOption: MultiFieldOption;
  if (field.attributes.display_type === 'multiSelect') {
    multiSelectFieldOption = field.attributes.valueOptions.find(opt => opt.conditionalPrefix);
  }

  if (conditional == null && conditionalPrefix == null && !multiSelectFieldOption) return of(field);

  // only conditional
  if (conditionalPrefix == null && !multiSelectFieldOption) {
    const updateValidator = toValidatorMutator(field.control);
    const determinator = fields[conditional.determinator_field].control;

    if (conditional.determinator_field == 'lease_end_date') {
      return determinator.valueChanges.pipe(
        startWith(determinator.value),
        map(value => (value != null ? 'true' : null)),
        map(value => {
          const consequence = conditional.consequences[value] || conditional.default_state;
          updateValidator(consequence.required);

          if (consequence.disabled) {
            field.control.disable();
          } else {
            field.control.enable();
          }

          if (consequence.label) {
            field.attributes.label = consequence.label;
          }
          if (consequence.display) return field;
          return null;
        })
      );
    }

    return determinator.valueChanges.pipe(
      startWith(determinator.value),
      map(value => (value != null ? value.toString() : null)),
      map(value => {
        const consequence = conditional.consequences[value] || conditional.default_state;
        if (consequence.required) {
          updateValidator(consequence.required);
        }

        if (consequence.disabled) {
          field.control.disable();
        } else {
          field.control.enable();
        }

        if (consequence.label) {
          field.attributes.label = consequence.label;
        }
        if (consequence.display) return field;
        return null;
      })
    );
  }

  // conditionalPrefix || multiSelectValueConditionalPrefix
  if (conditional == null) {
    const determinatorName = (
      conditionalPrefix ? conditionalPrefix : multiSelectFieldOption.conditionalPrefix
    ).determinator_field;
    const determinator = fields[determinatorName];
    const determinatorCurrencyOptions: CurrencyOption[] = [...determinator.attributes.valueOptions];

    return determinator.control.valueChanges.pipe(
      startWith(determinator.control.value),
      map(value => (value != null ? value.toString() : null)),
      map(value => {
        const selectedCurrencyOption: CurrencyOption = determinatorCurrencyOptions.find(
          opt => opt.id.toString() === value
        );

        if (selectedCurrencyOption) {
          const newPrefix = `${selectedCurrencyOption.source.iso3} ${selectedCurrencyOption.source.symbol}`;

          if (multiSelectFieldOption) {
            multiSelectFieldOption.prefix = newPrefix;
          } else {
            field.attributes.prefix = newPrefix;
          }
          field.attributeChanges.emit();
        }

        return field;
      })
    );
  }

  // conditional && (conditionalPrefix || multiSelectFieldOption)
  const updateValidator = toValidatorMutator(field.control);
  const determinator = fields[conditional.determinator_field].control;
  const determinatorNamePrefix = (
    conditionalPrefix ? conditionalPrefix : multiSelectFieldOption.conditionalPrefix
  ).determinator_field;
  const determinatorPrefix = fields[determinatorNamePrefix];
  const determinatorCurrencyOptions: CurrencyOption[] = [
    ...determinatorPrefix.attributes.valueOptions
  ];

  const determinatorValueObs = determinator.valueChanges.pipe(
    startWith(determinator.value),
    map(value => (value != null ? value.toString() : null))
  );

  const determinatorPrefixValueObs = determinatorPrefix.control.valueChanges.pipe(
    startWith(determinatorPrefix.control.value),
    map(value => (value != null ? value.toString() : null))
  );

  return combineLatest([determinatorValueObs, determinatorPrefixValueObs]).pipe(
    map(([conditionalValue, conditionalPrefixValue]) => {
      const consequence = conditional.consequences[conditionalValue] || conditional.default_state;
      updateValidator(consequence.required);

      if (consequence.disabled) {
        field.control.disable();
      } else {
        field.control.enable();
      }

      const selectedCurrencyOption: CurrencyOption = determinatorCurrencyOptions.find(
        opt => opt.id.toString() === conditionalPrefixValue
      );

      if (selectedCurrencyOption) {
        const newPrefix = `${selectedCurrencyOption.source.iso3} ${selectedCurrencyOption.source.symbol}`;

        if (multiSelectFieldOption) {
          multiSelectFieldOption.prefix = newPrefix;
        } else {
          field.attributes.prefix = newPrefix;
        }
        field.attributeChanges.emit();
      }

      if (consequence.label) {
        field.attributes.label = consequence.label;
      }
      if (consequence.display) return field;
      return null;
    })
  );
}

/**
 * Returns a function, which is able to extends a
 * control's validators with a "required" validator.
 *
 * Calling a returned function will update a validity of a given control.
 */
function toValidatorMutator(control: UntypedFormControl) {
  const defaultValidator = control.validator || (() => null);

  return (required?: boolean) => {
    if (required) {
      control.setValidators([defaultValidator, requiredValidator]);
    } else {
      control.setValidators(defaultValidator);
    }
    control.updateValueAndValidity({ emitEvent: false });
  };
}

/**
 * Returns TRUE when a given `field` has an renderable display type.
 */
export function isRenderable(field: Field): boolean {
  return field.attributes.display_type !== 'hidden';
}

/**
 * Returns TRUE when a given `field` has flag 'custom' set  to true.
 */
export function isCustom(field: Field): boolean {
  return field.attributes.custom === true;
}

/**
 * Discards changes in a given `fields`.
 */
export function discardChanges(fields: Observable<Fields>) {
  fields.pipe(first(), map(values)).subscribe(fields => {
    fields.forEach(({ control, defaultValue }) => control.reset(defaultValue));
  });
}

/**
 * Updates the given values in the given Field spec.
 */
export function updateFieldsSpecValues(
  spec: FieldsSpec,
  newValues: Record<string, any>
): FieldsSpec {
  return toPairs(newValues).reduce(
    (acc, [name, value]) => ({ ...acc, [name]: { ...spec[name], value } }),
    spec
  );
}

/**
 * Returns a copy of the provided field but with another label
 */
export function createFromFieldWithNewLabel(
  field: Field,
  newLabel: string
): Field {
  if (field) {
    field.attributes.label = newLabel;
  }
  return field;
}

/**
 * Returns a copy of the provided field but disabled
 */
export function disableField(
  field: Field
): Field {
  if (field) {
    field.attributes.disabled = true;
    field.control.disable();
  }
  return field;
}

/**
 * Returns a copy of the provided field but without any validators
 */
export function removeAllValidatorsFromField(
  field: Field
): Field {
  if (field) {
    field.control.clearValidators();
    field.control.updateValueAndValidity();
  }
  return field;
}

/**
 * Returns a copy of the provided field but with the 'required' validator
 */
export function makeFieldRequired(
  field: Field
): Field {
  if (field) {
    field.control.addValidators(requiredValidator);
  }
  return field;
}

// Helper to clone a control without sharing subscriptions/refs
export function cloneControlAsUntyped(source: any): UntypedFormControl {
  // Preserve value and disabled state, and copy validators
  return new UntypedFormControl(
    {value: source?.value ?? null, disabled: !!source?.disabled},
    source?.validator ?? null,
    source?.asyncValidator ?? null
  );
}

export function isConditionalFieldDisplayed(field: FieldSpec, fields: FieldsSpec) {
  const validTrueValues = Object.entries(field.attributes.conditional.consequences)
    .filter(([key, value]) => {
      return value['display'] === true;
    })
    .map(([key, value]) => key);

  const validFalseValues = Object.entries(field.attributes.conditional.consequences)
    .filter(([key, value]) => {
      return value['display'] === false;
    })
    .map(([key, value]) => key);

  if (field.attributes.conditional.default_state['display'] === false &&
    validTrueValues.includes(fields[field.attributes.conditional.determinator_field].value.toString())) {
    return true;
  }

  if (field.attributes.conditional.default_state['display'] === true &&
    validFalseValues.includes(fields[field.attributes.conditional.determinator_field].value.toString())) {
    return false;
  }

}
